import { Fragment, useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ToastService } from '@iamsoftware/react-hooks';
import { useDataTableBasic } from 'src/hooks/useAppDataTable';

interface UploadExcelService {
    toFormData: (data: any) => FormData;
    uploadExcel: (data: any) => Promise<any>;
    baseURL: string;
    entity: string;
}

interface UploadExcelProps<T = any> {
    display: boolean;
    setDisplay: (v: boolean) => void;
    reloadLazyData: () => void;
    service: UploadExcelService;
    header?: string;
    resultHeader?: string;
    columns?: any[];
    downloadTemplate?: () => void;
}

const defaultColumns = [
    { field: 'rowNum', header: 'Dòng dữ liệu', width: 110, dataType: 'number' },
    {
        field: 'success', header: 'Kết quả', width: 100, dataType: 'custom', customCell(rowData: any): any {
            if (rowData.success === 'Y') {
                return <p className="m-0"><i className="true-icon pi pi-check-circle mr-1" /><span>Thành công</span></p>;
            }
            return <p className="m-0"><i className="false-icon pi pi-times-circle mr-1" /><span>Thất bại</span></p>;
        }
    },
    {
        field: 'error', header: 'Mô tả lỗi', minWidth: 300, dataType: 'custom', customCell(rowData: any) {
            if (rowData.errorList && Array.isArray(rowData.errorList) && rowData.errorList.length > 0) {
                return (
                    <ul className="list-none m-0 p-0">
                        {rowData.errorList.map((err: string, idx: number) => (
                            <li key={idx} className="text-pink-500 mb-1 flex align-items-start">
                                <i className="pi pi-minus mr-1 mt-1" style={{ fontSize: '0.7rem' }} />
                                <span>{err}</span>
                            </li>
                        ))}
                    </ul>
                );
            }
            return <div style={{ whiteSpace: 'pre-wrap' }}>{rowData.error}</div>;
        }
    }
];

export function UploadExcel<T = any>({
    display, setDisplay, reloadLazyData, service,
    header = 'Upload file Excel',
    resultHeader = 'Kết quả upload Excel',
    columns,
    downloadTemplate,
}: UploadExcelProps<T>) {

    const inputSignedFile = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploadResult, setUploadResult] = useState<T[]>([]);
    const [displayResult, setDisplayResult] = useState<any>(null);

    const onUploadSignedFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target?.files?.length === 1) {
            setFile(event.target.files[0]);
        } else {
            ToastService.error('Chưa chọn file Excel');
        }
    };

    const handleDownloadTemplate = () => {
        if (downloadTemplate) {
            downloadTemplate();
        } else {
            window.open(`${service.baseURL}/${service.entity}/download-template`);
        }
    };

    const { render: renderDataTableItem } = useDataTableBasic({
        tableHeader: resultHeader,
        dataKey: 'rowNum',
        columns: columns || defaultColumns,
        items: uploadResult
    });

    const onSubmit = () => {
        if (!file) {
            ToastService.error('Vui lòng chọn file Excel');
            return;
        }
        service.uploadExcel(service.toFormData({ contentFile: file })).then((data: any) => {
            setUploadResult(data.resultList || []);
            setDisplayResult(Date.now());
            reloadLazyData();
        }).catch((error: any) => {
            ToastService.error(error?.response?.data?.errors || error?.message || 'Có lỗi xảy ra khi upload file Excel');
            close();
        });
        if (inputSignedFile.current) {
            inputSignedFile.current.value = '';
        }
    };

    const close = () => {
        setDisplay(false);
        setDisplayResult(null);
    };

    useEffect(() => {
        if (!display) {
            setFile(null);
            if (inputSignedFile.current) {
                inputSignedFile.current.value = '';
            }
        }
    }, [display]);

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Tải file mẫu" icon="pi pi-download" className="p-button-outlined p-button-help p-button-sm" type="button" onClick={handleDownloadTemplate} />
            <Button label="Tải lên" icon="pi pi-upload" className="p-button-outlined p-button-success p-button-sm" type="button" onClick={onSubmit} />
            <Button label="Đóng" icon="pi pi-times" className="p-button-outlined p-button-secondary p-button-sm" type="button" onClick={() => setDisplay(false)} />
        </div>
    );

    const resultFooter = (
        <div className="flex justify-content-end">
            <Button label="Đóng" icon="pi pi-times" className="p-button-outlined p-button-secondary p-button-sm" type="button" onClick={() => setDisplayResult(null)} />
        </div>
    );

    return (
        <Fragment>
            <Dialog header={header} draggable={false} footer={footer} visible={!!display} style={{ width: '40rem' }} position="top" onHide={() => setDisplay(false)}>
                <div className="grid">
                    <div className="col-12">
                        <div>Chọn file Excel<span style={{ color: 'red' }}>*</span></div>
                        <input
                            className="mt-2 w-full p-1 border-1 surface-border border-round cursor-pointer"
                            style={{ backgroundColor: 'var(--surface-card)', color: 'var(--text-color)' }}
                            ref={inputSignedFile}
                            type="file"
                            accept='.xlsx'
                            onChange={onUploadSignedFile}
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog header={resultHeader} footer={resultFooter} visible={!!displayResult} style={{ width: '80rem' }} position="top" onHide={() => close()}>
                {renderDataTableItem()}
            </Dialog>
        </Fragment>
    );
}
